import React, { useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../../hooks/useUntypedTranslation';
import { eyebrow, focusRing, meta, pillAction, pressable } from '../../../../styles/visualLanguage';
import { AdvisoryLeaveButton } from '../AdvisoryLeaveButton';
import { AdvisoryWorkspaceSources, WorkspaceStatus } from './AdvisoryWorkspaceSources';
import type {
  AdvisoryEngagementWorkspaceProps,
  AdvisoryWorkspaceStage,
} from './advisoryWorkspaceTypes';

export const WORKSPACE_STAGES: readonly AdvisoryWorkspaceStage[] = [
  'companies',
  'exploration',
  'answers',
];

/** Pure workspace view. Stage navigation never starts work; readiness belongs to the controller. */
export function AdvisoryEngagementWorkspace(props: AdvisoryEngagementWorkspaceProps) {
  const { t } = useUntypedTranslation();
  const hasCompanyResearch = props.companies.length > 0;
  const previousStarted = useRef(props.started);
  const details = useRef<Partial<Record<AdvisoryWorkspaceStage, HTMLElement | null>>>({});
  useEffect(() => {
    if (props.started && !previousStarted.current) details.current[props.selectedStage]?.focus();
    previousStarted.current = props.started;
  }, [props.started, props.selectedStage]);
  const canStart =
    props.canStart &&
    !props.locked &&
    !props.started &&
    !props.startPending &&
    props.startBlockers.length === 0;
  return (
    <Box
      data-testid="advisory-engagement-workspace"
      sx={theme => ({
        backgroundColor: theme.palette.background.default,
        color: theme.colors.inkPrimary,
        maxWidth: 960,
        mx: 'auto',
        px: { xs: 2.5, sm: 4.5 },
        py: 3,
      })}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography sx={[eyebrow]}>{t('advisory:workspace.eyebrow')}</Typography>
        <AdvisoryLeaveButton />
      </Box>
      <Typography
        component="h1"
        sx={{
          fontSize: { xs: 28, sm: 34 },
          lineHeight: 1.2,
          fontWeight: 500,
          letterSpacing: '-0.5px',
        }}
      >
        {t('advisory:workspace.title')}
      </Typography>
      <Typography sx={[meta, { mt: 1.5 }]}>{props.goal}</Typography>

      <Box
        component="nav"
        aria-label={t('advisory:workspace.stageNavigation')}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          columnGap: 3,
          rowGap: 1.5,
          my: 4,
        }}
      >
        {WORKSPACE_STAGES.map((stage, index) => (
          <Button
            key={stage}
            type="button"
            id={'workspace-stage-' + stage}
            aria-pressed={props.selectedStage === stage}
            aria-controls={'workspace-detail-' + stage}
            onClick={() => props.onSelectStage(stage)}
            sx={[
              focusRing,
              theme => ({
                borderRadius: 0,
                borderTop: `3px solid ${
                  props.selectedStage === stage
                    ? theme.colors.lavenderDeep
                    : theme.visualPrimitives.border
                }`,
                p: '12px 0 0',
                display: 'grid',
                gridTemplateColumns: { xs: '28px 1fr', sm: '1fr' },
                textAlign: 'left',
                justifyItems: 'start',
                alignContent: 'start',
                color: theme.colors.inkPrimary,
                textTransform: 'none',
                '&:hover': { backgroundColor: theme.palette.background.default },
              }),
            ]}
          >
            <Typography component="span" sx={[eyebrow, { fontSize: 11 }]}>
              {String(index + 1).padStart(2, '0')}
            </Typography>
            <Typography component="span" sx={{ fontSize: 18, lineHeight: 1.3 }}>
              {t('advisory:workspace.stage.' + stage)}
            </Typography>
            <Typography
              component="span"
              sx={[meta, { mt: 1, fontSize: 12, gridColumn: { xs: '2', sm: '1' } }]}
            >
              <WorkspaceStatus
                status={
                  stage === 'companies' && !hasCompanyResearch ? 'notRequired' : props.stages[stage]
                }
              />
            </Typography>
          </Button>
        ))}
      </Box>

      {/* Keep panels mounted: changing the selected detail cannot discard an editor draft. */}
      <Box
        id="workspace-detail-companies"
        component="section"
        tabIndex={-1}
        ref={(node: HTMLElement | null) => {
          details.current.companies = node;
        }}
        aria-labelledby="workspace-stage-companies"
        hidden={props.selectedStage !== 'companies'}
        sx={theme => ({ borderTop: `1px solid ${theme.visualPrimitives.border}` })}
      >
        <AdvisoryWorkspaceSources
          {...props}
          locked={props.locked || props.started || Boolean(props.startPending)}
        />
      </Box>
      <Box
        id="workspace-detail-exploration"
        component="section"
        tabIndex={-1}
        ref={(node: HTMLElement | null) => {
          details.current.exploration = node;
        }}
        aria-labelledby="workspace-stage-exploration"
        hidden={props.selectedStage !== 'exploration'}
        sx={{ py: 2 }}
      >
        {props.started ? (
          props.explorationPanel ?? (
            <Typography sx={[meta]}>{t('advisory:workspace.waitingProgress')}</Typography>
          )
        ) : (
          <Typography sx={[meta]}>{t('advisory:workspace.explorationHint')}</Typography>
        )}
      </Box>
      <Box
        hidden={
          props.selectedStage === 'answers' ||
          (props.started && props.selectedStage !== 'exploration')
        }
        component="section"
        aria-labelledby="workspace-questions-heading"
        sx={theme => ({ py: 3, borderTop: `1px solid ${theme.visualPrimitives.border}` })}
      >
        <Typography component="h2" id="workspace-questions-heading" variant="h6">
          {t(
            props.started
              ? 'advisory:workspace.lockedQuestions'
              : 'advisory:workspace.questionsTitle'
          )}
        </Typography>
        <Typography sx={[meta, { mt: 1 }]}>
          {t(
            props.started
              ? 'advisory:workspace.lockedHint'
              : hasCompanyResearch
              ? 'advisory:workspace.questionsHint'
              : 'advisory:workspace.questionsHintNoCompanies'
          )}
        </Typography>
        {props.questionPanel}
      </Box>
      <Box
        id="workspace-detail-answers"
        component="section"
        tabIndex={-1}
        ref={(node: HTMLElement | null) => {
          details.current.answers = node;
        }}
        aria-labelledby="workspace-stage-answers"
        hidden={props.selectedStage !== 'answers'}
        sx={{ py: 3 }}
      >
        {props.answerPanel ?? (
          <>
            <Typography component="h2" variant="h6">
              {t('advisory:workspace.answerTitle')}
            </Typography>
            <Typography sx={[meta, { mt: 1 }]}>{t('advisory:workspace.answerHint')}</Typography>
          </>
        )}
      </Box>

      {props.statusKey && (
        <Typography role="status" sx={[meta, { py: 2 }]}>
          {t(props.statusKey)}
        </Typography>
      )}
      <Box
        component="footer"
        sx={theme => ({
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 3,
          pt: 3,
          borderTop: `1px solid ${theme.visualPrimitives.border}`,
        })}
      >
        <Box id="workspace-readiness" sx={{ maxWidth: 560 }} aria-live="polite">
          {props.started ? (
            <Typography sx={[meta]}>{t('advisory:workspace.leaveHint')}</Typography>
          ) : props.startBlockers.length ? (
            props.startBlockers.map(key => (
              <Typography key={key} sx={[meta]}>
                {t(key)}
              </Typography>
            ))
          ) : (
            <Typography sx={[meta]}>
              {t(
                props.canStart && !props.locked
                  ? 'advisory:workspace.readyHint'
                  : 'advisory:workspace.waitingReadiness'
              )}
            </Typography>
          )}
        </Box>
        {!props.started && (
          <Button
            type="button"
            variant="contained"
            disabled={!canStart}
            aria-describedby="workspace-readiness"
            onClick={() => {
              if (canStart) props.onStart();
            }}
            sx={[pillAction, pressable, focusRing, { flexShrink: 0 }]}
          >
            {t(props.startPending ? 'advisory:workspace.starting' : 'advisory:workspace.start')}
          </Button>
        )}
      </Box>
    </Box>
  );
}
